import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${API_BASE}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('rw_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rw_token');
      localStorage.removeItem('rw_user');
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  deleteAccount: (data) => api.delete('/auth/account', { data }),
};

// Sites
export const sitesAPI = {
  getAll: () => api.get('/sites'),
  getOne: (id) => api.get(`/sites/${id}`),
};

// Activities
export const activitiesAPI = {
  getAll: (siteId) => api.get('/activities', { params: siteId ? { site_id: siteId } : {} }),
  getOne: (id) => api.get(`/activities/${id}`),
};

// Bookings
export const bookingsAPI = {
  getSlots: (activityId, date, durationMinutes) =>
    api.get('/bookings/available-slots', { params: { activity_id: activityId, date, duration_minutes: durationMinutes } }),
  create: (data) => api.post('/bookings', data),
  createMulti: (data) => api.post('/bookings/multi', data),
  getMy: () => api.get('/bookings/my'),
  cancel: (id) => api.post(`/bookings/${id}/cancel`),
  modify: (id, data) => api.put(`/bookings/${id}/modify`, data),
  getAvailableSlots: (activityId, date, durationMinutes) =>
    api.get('/bookings/available-slots', { params: { activity_id: activityId, date, duration_minutes: durationMinutes } }),
  // Contract
  getContract: (bookingId) => api.get(`/bookings/${bookingId}/contract`),
  acceptContract: (bookingId, data) => api.post(`/bookings/${bookingId}/contract/accept`, data),
  getObligations: () => api.get('/contract-obligations'),
  // Participants
  getParticipants: (bookingId) => api.get(`/bookings/${bookingId}/participants`),
  registerParticipants: (bookingId, data) => api.post(`/bookings/${bookingId}/participants`, data),
  // Photos
  getBookingPhotos: (bookingId) => api.get(`/bookings/${bookingId}/photos`),
  getPhotoUrl: (photoId) => `${API_BASE}/api/photos/${photoId}/download?auth=${localStorage.getItem('rw_token')}`,
};

// Payments
export const paymentsAPI = {
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  confirmRevolutBooking: (groupId) => api.get(`/bookings/confirm-revolut/${groupId}`),
};

// Exports (ZIP CSVs)
export const exportsAPI = {
  adminExportClients: (siteId) => api.get('/admin/export/clients', { params: siteId ? { site_id: siteId } : {}, responseType: 'blob' }),
  exportMyData: () => api.get('/me/export', { responseType: 'blob' }),
};

// Gift Cards
export const giftCardsAPI = {
  purchase: (data) => api.post('/gift-cards/purchase', data),
  validate: (code) => api.post('/gift-cards/validate', { code }),
  getMy: () => api.get('/gift-cards/my'),
  confirm: (gcId) => api.get(`/gift-cards/confirm/${gcId}`),
};

// Promo Codes
export const promoCodesAPI = {
  validate: (data) => api.post('/promo-codes/validate', data),
};

// Credits
export const creditsAPI = {
  getPackages: (siteId) => api.get('/credit-packages', { params: siteId ? { site_id: siteId } : {} }),
  purchase: (data) => api.post('/credits/purchase', data),
  getBalance: () => api.get('/credits/balance'),
  getHistory: () => api.get('/credits/history'),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getBookings: () => api.get('/admin/bookings'),
  getBookingDetail: (bookingId) => api.get(`/admin/bookings/${bookingId}`),
  updateBooking: (bookingId, data) => api.patch(`/admin/bookings/${bookingId}`, data),
  deleteBooking: (bookingId) => api.delete(`/admin/bookings/${bookingId}`),
  confirmOnsitePayment: (id, data) => api.post(`/admin/bookings/${id}/confirm-onsite`, data || {}),
  createBooking: (data) => api.post('/admin/bookings/create', data),
  getActivities: () => api.get('/admin/activities'),
  createActivity: (data) => api.post('/admin/activities', data),
  updateActivity: (id, data) => api.put(`/admin/activities/${id}`, data),
  deleteActivity: (id) => api.delete(`/admin/activities/${id}`),
  getSites: () => api.get('/admin/sites'),
  updateSite: (id, data) => api.put(`/admin/sites/${id}`, data),
  createSite: (data) => api.post('/admin/sites', data),
  deleteSite: (id) => api.delete(`/admin/sites/${id}`),
  getGiftCards: () => api.get('/admin/gift-cards'),
  createGiftCard: (data) => api.post('/admin/gift-cards/create', data),
  getGiftCardDetail: (id) => api.get(`/admin/gift-cards/${id}`),
  editGiftCard: (id, data) => api.patch(`/admin/gift-cards/${id}`, data),
  getGiftCardPdfUrl: (id) => `${API_BASE}/api/admin/gift-cards/${id}/pdf?token=${localStorage.getItem('rw_token')}`,
  sendGiftCardEmail: (id, data) => api.post(`/admin/gift-cards/${id}/send-email`, data),
  getPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  updatePromoCode: (id, data) => api.patch(`/admin/promo-codes/${id}`, data),
  deletePromoCode: (id) => api.delete(`/admin/promo-codes/${id}`),
  getTransactions: () => api.get('/admin/transactions'),
  exportTransactions: (params) => api.get('/admin/transactions/export', { params, responseType: 'blob' }),
  getClients: () => api.get('/admin/clients'),
  createClient: (data) => api.post('/admin/clients', data),
  updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/admin/clients/${id}`),
  getClientBookings: (id) => api.get(`/admin/clients/${id}/bookings`),
  getResources: () => api.get('/admin/resources'),
  createResource: (data) => api.post('/admin/resources', data),
  updateResource: (id, data) => api.put(`/admin/resources/${id}`, data),
  deleteResource: (id) => api.delete(`/admin/resources/${id}`),
  getCalendar: (startDate, endDate, siteId) => api.get('/admin/calendar', { params: { start_date: startDate, end_date: endDate, ...(siteId ? { site_id: siteId } : {}) } }),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (siteId, data) => api.put(`/admin/settings/${siteId}`, data),
  testEmail: (siteId) => api.post(`/admin/settings/${siteId}/test-email`),
  // Email templates
  getEmailTemplates: (siteId) => api.get(`/admin/email-templates/${siteId}`),
  updateEmailTemplate: (siteId, emailType, data) => api.put(`/admin/email-templates/${siteId}/${emailType}`, data),
  resetEmailTemplate: (siteId, emailType) => api.post(`/admin/email-templates/reset/${siteId}/${emailType}`),
  testEmailTemplate: (data) => api.post('/admin/email-templates/test', data),
  getHomepageSettings: () => api.get('/admin/homepage-settings'),
  updateHomepageSettings: (data) => api.put('/admin/homepage-settings', data),
  // Legal pages
  getLegalPage: (pageType) => api.get(`/admin/legal/${pageType}`),
  updateLegalPage: (pageType, data) => api.put(`/admin/legal/${pageType}`, data),
  getRemainingSlots: (siteId, date) => api.get(`/admin/remaining-slots`, { params: { site_id: siteId, ...(date ? { date } : {}) } }),
  sendPromo: (data) => api.post('/admin/send-promo', data),
  // Backups
  getBackups: () => api.get('/admin/backups'),
  createBackup: () => api.post('/admin/backups/create'),
  downloadBackup: (filename) => api.get(`/admin/backups/download/${filename}`, { responseType: 'blob' }),
  restoreBackup: (filename) => api.post(`/admin/backups/restore/${filename}`),
  deleteBackup: (filename) => api.delete(`/admin/backups/${filename}`),
  exportToAtlas: () => api.post('/admin/backups/export-atlas'),
  // Admin user management
  getAdminUsers: () => api.get('/admin/admin-users'),
  createAdminUser: (data) => api.post('/admin/admin-users', data),
  updateAdminUser: (id, data) => api.put(`/admin/admin-users/${id}`, data),
  deleteAdminUser: (id) => api.delete(`/admin/admin-users/${id}`),
  // Credit packages
  getCreditPackages: () => api.get('/admin/credit-packages'),
  createCreditPackage: (data) => api.post('/admin/credit-packages', data),
  updateCreditPackage: (id, data) => api.put(`/admin/credit-packages/${id}`, data),
  deleteCreditPackage: (id) => api.delete(`/admin/credit-packages/${id}`),
  grantCredits: (data) => api.post('/admin/credits/grant', data),
  deductCredits: (data) => api.post('/admin/credits/deduct', data),
  setCreditsBalance: (data) => api.post('/admin/credits/set-balance', data),
  getUserCredits: (userId) => api.get(`/admin/credits/user/${userId}`),
  getCreditBalances: () => api.get('/admin/credits/balances'),
  getCreditTransactions: () => api.get('/admin/credits/transactions'),
  // Blocked slots
  getBlockedSlots: (siteId) => api.get('/admin/blocked-slots', { params: siteId ? { site_id: siteId } : {} }),
  createBlockedSlot: (data) => api.post('/admin/blocked-slots', data),
  deleteBlockedSlot: (id) => api.delete(`/admin/blocked-slots/${id}`),
  // Flash promos
  getFlashPromos: (siteId) => api.get('/admin/flash-promos', { params: siteId ? { site_id: siteId } : {} }),
  deactivateFlashPromo: (promoId) => api.patch(`/admin/flash-promos/${promoId}/deactivate`),
  sendPromoBatch: (data) => api.post('/admin/send-promo-batch', data),
  // Contract obligations
  getContractObligations: () => api.get('/admin/contract-obligations'),
  updateContractObligations: (data) => api.put('/admin/contract-obligations', data),
  getBookingContract: (bookingId) => api.get(`/admin/bookings/${bookingId}/contract`),
  getBookingContractPdf: (bookingId) => `${API_BASE}/api/admin/bookings/${bookingId}/contract/pdf?token=${localStorage.getItem('rw_token')}`,
  notifyClient: (bookingId, data) => api.post(`/admin/bookings/${bookingId}/notify`, data),
  // Photos
  uploadBookingPhoto: (bookingId, formData) => api.post(`/admin/bookings/${bookingId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getBookingPhotos: (bookingId) => api.get(`/admin/bookings/${bookingId}/photos`),
  deletePhoto: (photoId) => api.delete(`/admin/photos/${photoId}`),
  getPhotoUrl: (photoId) => `${API_BASE}/api/photos/${photoId}/download?auth=${localStorage.getItem('rw_token')}`,
  // Client detail
  getClientDetail: (clientId) => api.get(`/admin/clients/${clientId}`),
  // Google Review
  updateGoogleReview: (siteId, data) => api.put(`/admin/sites/${siteId}/google-review`, data),
  getGoogleReview: (siteId) => api.get(`/admin/sites/${siteId}/google-review`),
  uploadBase64: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await api.post('/upload/base64', {
            data: reader.result,
            filename: file.name,
          }, { timeout: 60000 });
          resolve(res);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Erreur lecture fichier'));
      reader.readAsDataURL(file);
    });
  },
  uploadPdf: async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await api.post('/upload/base64', {
            data: reader.result,
            filename: file.name,
          }, { timeout: 60000 });
          resolve(res);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Erreur lecture fichier'));
      reader.readAsDataURL(file);
    });
  },
};

export const siteInfoAPI = {
  getAll: () => api.get('/site-info'),
};

export const homepageAPI = {
  get: () => api.get('/homepage-settings'),
};

export default api;

// Utility to resolve image URLs (handles /uploads/ and /api/uploads/ paths)
export const resolveImageUrl = (url, fallback) => {
  if (!url) return fallback || '';
  if (url.startsWith('/api/uploads')) return `${API_BASE}${url}`;
  if (url.startsWith('/uploads')) return `${API_BASE}/api${url}`;
  return url;
};
